import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabaseClient';

/**
 * FeaturedHighlights renders the hero banner block on the homepage using data from Supabase.
 * Records flagged with variant === 'hero' take the large slot, otherwise the first record becomes the hero.
 * Additional items flow into a responsive grid so the section scales gracefully when more cards are added.
 */

const sanitizeSegment = (value) => {
  if (!value) return null;
  const trimmed = value.toString().trim();
  if (!trimmed || trimmed === '/' || trimmed === '#') return null;
  return trimmed.replace(/^\/+|\/+$/g, '');
};

const prettifySlug = (slug) => {
  if (!slug) return null;
  const clean = slug
    .toString()
    .split('/')
    .filter(Boolean)
    .pop();

  if (!clean) return null;

  return clean
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const buildCtaLabel = (item) => {
  if (!item) return 'Ver más';

  if (item.cta_label) {
    return item.cta_label;
  }

  const type = (item.link_type || '').toLowerCase();
  const rawValue = item.link_value;

  const prioritizedSlug = sanitizeSegment(rawValue);

  const prettyLabel =
    (type === 'search' && rawValue ? `Buscar ${rawValue}` : null) ||
    prettifySlug(prioritizedSlug) ||
    (item.title ? `Ver ${item.title}` : null);

  if (prettyLabel) {
    return prettyLabel;
  }

  return 'Ver más';
};

const FeaturedHighlights = ({ onNavigate }) => {
  const [highlights, setHighlights] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchHighlights = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('homepage_highlights')
          .select('*')
          .eq('is_active', true)
          .order('position', { ascending: true });

        if (error) {
          throw error;
        }

        const now = new Date();
        const filtered = (data || []).filter((item) => {
          if (!item) return false;
          const startsOk = !item.starts_at || new Date(item.starts_at) <= now;
          const endsOk = !item.ends_at || new Date(item.ends_at) >= now;
          return startsOk && endsOk;
        });

        if (isMounted) {
          setHighlights(filtered);
        }
      } catch (err) {
        console.error('Error loading homepage highlights:', err);
        if (isMounted) {
          setHighlights([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchHighlights();

    return () => {
      isMounted = false;
    };
  }, []);

  const { hero, rest } = useMemo(() => {
    if (!highlights.length) {
      return { hero: null, rest: [] };
    }

    const heroIndex = highlights.findIndex(
      (item) => item?.variant && item.variant.toLowerCase() === 'hero'
    );

    if (heroIndex > -1) {
      const heroItem = highlights[heroIndex];
      const restItems = [
        ...highlights.slice(0, heroIndex),
        ...highlights.slice(heroIndex + 1),
      ];
      return { hero: heroItem, rest: restItems };
    }

    return {
      hero: highlights[0],
      rest: highlights.slice(1),
    };
  }, [highlights]);

  const handleNavigate = useCallback(
    (item, options = {}) => {
      if (!item) return;

      const { preferNewTab = false } = options;
      const openInNewTab = preferNewTab || item.open_in_new_tab;
      const type = (item.link_type || '').toLowerCase();
      const value = item.link_value;

      const navigate = (path) => {
        if (!path) return;
        if (openInNewTab && typeof window !== 'undefined') {
          const url = new URL(path, window.location.origin).toString();
          window.open(url, '_blank', 'noopener,noreferrer');
          return;
        }
        if (typeof onNavigate === 'function') {
          onNavigate(path);
        } else if (typeof window !== 'undefined') {
          window.location.assign(path);
        }
      };

      const openExternal = (url) => {
        if (!url) return;
        if (typeof window !== 'undefined') {
          window.open(url, openInNewTab ? '_blank' : '_self', 'noopener,noreferrer');
        }
      };

      if (type === 'url') {
        openExternal(value);
        return;
      }

      if (type === 'search') {
        const term = value || sanitizeSegment(value);
        if (term) {
          navigate(`/search/${encodeURIComponent(term)}`);
        }
        return;
      }

      if (type === 'category') {
        const category = sanitizeSegment(value);
        navigate(category ? `/${encodeURIComponent(category)}` : '/');
        return;
      }

      if (type === 'product') {
        const product = sanitizeSegment(value);
        if (product) {
          navigate(`/product/${encodeURIComponent(product)}`);
        } else if (value) {
          navigate(value);
        }
        return;
      }

      if (type === 'path') {
        if (value) {
          navigate(value.startsWith('/') ? value : `/${value}`);
        }
        return;
      }

      if (value) {
        if (/^https?:\/\//i.test(value)) {
          openExternal(value);
          return;
        }

        if (value.startsWith('/')) {
          navigate(value);
          return;
        }

        const sanitized = sanitizeSegment(value);
        navigate(sanitized ? `/${encodeURIComponent(sanitized)}` : value);
      }
    },
    [onNavigate]
  );

  if (isLoading && !highlights.length) {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-20 mb-16">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-stretch">
          <div className="animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700 h-[220px] sm:h-[320px] xl:h-[420px]" />
          <div className="flex flex-col justify-between gap-6">
            <div className="animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700 h-[160px] sm:h-[180px]" />
            <div className="animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700 h-[160px] sm:h-[180px]" />
          </div>
        </div>
      </section>
    );
  }

  if (!hero && !rest.length) {
    return null;
  }

  const topColumnItems = rest.slice(0, 2);
  const additionalItems = rest.slice(2);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-20 mb-16">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-stretch">
        {hero && (
          <HighlightCard
            key={hero.id}
            item={hero}
            variant={topColumnItems.length ? 'hero' : 'solo'}
            onNavigate={handleNavigate}
          />
        )}

        {topColumnItems.length > 0 && (
          <div className="flex flex-col justify-between gap-6">
            {topColumnItems.map((item) => (
              <HighlightCard
                key={item.id}
                item={item}
                variant="standard"
                onNavigate={handleNavigate}
              />
            ))}
          </div>
        )}
      </div>

      {additionalItems.length > 0 && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {additionalItems.map((item) => (
            <HighlightCard
              key={item.id}
              item={item}
              variant="standard"
              onNavigate={handleNavigate}
            />
          ))}
        </div>
      )}
    </section>
  );
};

const HighlightCard = ({ item, variant, onNavigate }) => {
  if (!item) return null;

  const isHero = variant === 'hero';
  const spanAll = variant === 'solo';

  const baseClasses =
    'shine relative overflow-hidden rounded-xl shadow-sm transition-transform duration-500 ease-out group h-full';

  const sizingClasses = (() => {
    if (isHero) {
      return 'xl:col-span-2 min-h-[220px] sm:min-h-[320px] xl:min-h-[420px]';
    }
    if (spanAll) {
      return 'xl:col-span-3 min-h-[220px] sm:min-h-[320px] xl:min-h-[420px]';
    }
    return 'min-h-[160px] sm:min-h-[200px]';
  })();

  const animationDirection = item?.animation || (isHero ? 'fade-up-right' : 'fade-up-left');

  const handleCtaClick = (event) => {
    event.stopPropagation();
    onNavigate?.(item);
  };

  const ctaLabel = buildCtaLabel(item);

  return (
    <div
      data-aos={animationDirection}
      data-aos-duration={item?.animation_duration || 1000}
      data-aos-delay={item?.animation_delay || undefined}
      className={`${baseClasses} ${sizingClasses}`}
    >
      {item.image_url ? (
        <img
          loading="lazy"
          decoding="async"
          src={item.image_url}
          alt={item.alt_text || item.title || ''}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      ) : (
        <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-500 text-sm">
          Imagen no disponible
        </div>
      )}

      {(item.title || item.subtitle || ctaLabel) && (
        <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/60 via-black/10 to-transparent p-6 text-white">
          {item.title ? (
            <h2 className="text-xl sm:text-2xl font-semibold drop-shadow-md">
              {item.title}
            </h2>
          ) : null}
          {item.subtitle ? (
            <p className="mt-1 text-sm sm:text-base text-white/90 drop-shadow">
              {item.subtitle}
            </p>
          ) : null}
          {ctaLabel ? (
            <div className="mt-4 sm:mt-5">
              <button
                type="button"
                className="fancy-btn"
                onClick={handleCtaClick}
              >
                <span className="btn-text">Ver más.</span>
                <span className="icon-circle">
                  <span className="arrow" />
                </span>
              </button>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default FeaturedHighlights;
